import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Ban, Send } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';

/**
 * Why a shipment was cancelled, and — when it went out anyway under ANOTHER AWB / service provider —
 * the reference number and remarks for that dispatch. Shown on the order page and in the tracking
 * panel (client, 03-11). Staff can record the re-dispatch on a cancelled order; reference number and
 * remarks are required, the provider optional.
 */
export function CancelDispatchPanel({ order, canEdit, onSaved }: { order: any; canEdit: boolean; onSaved?: () => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState('');
  const [courier, setCourier] = useState('');
  const [remarks, setRemarks] = useState('');
  const cancelled = [23, 33].includes(Number(order?.statusId));
  const save = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/alt-dispatch`, { referenceNo: ref.trim(), courierName: courier.trim() || undefined, remarks: remarks.trim() })).data,
    onSuccess: () => {
      toast.success('Re-dispatch details saved');
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['order', String(order.id)] });
      qc.invalidateQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && q.queryKey[0] === 'orders' });
      onSaved?.();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save'),
  });
  const fmt = (d?: string | null) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '');
  if (!cancelled && !order?.cancelRemarks && !order?.altDispatchRef) return null;

  return (
    <div className="bg-white border border-rose-200 rounded-xl p-4 mt-4" data-testid="cancel-dispatch-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-semibold text-slate-800 flex items-center gap-2"><Ban size={16} className="text-rose-600" /> Cancellation</div>
        {canEdit && cancelled && (
          <button type="button" data-testid="alt-dispatch-open"
            onClick={() => { setRef(order.altDispatchRef ?? ''); setCourier(order.altDispatchCourier ?? ''); setRemarks(order.altDispatchRemarks ?? ''); setOpen(true); }}
            className="text-xs px-2.5 py-1.5 border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 inline-flex items-center gap-1">
            <Send size={12} /> {order.altDispatchRef ? 'Edit re-dispatch' : 'Dispatched via other AWB / courier'}
          </button>
        )}
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        <div><dt className="text-[11px] uppercase text-slate-400">Cancel remarks</dt><dd className="text-slate-800" data-testid="cancel-remarks-value">{order.cancelRemarks || '—'}</dd></div>
        <div><dt className="text-[11px] uppercase text-slate-400">Cancelled by</dt><dd className="text-slate-800">{[order.cancelledByName, fmt(order.cancelledAt)].filter(Boolean).join(' · ') || '—'}</dd></div>
        {order.altDispatchRef && <>
          <div><dt className="text-[11px] uppercase text-slate-400">Re-dispatched via</dt><dd className="text-slate-800" data-testid="alt-dispatch-value"><b>{order.altDispatchRef}</b>{order.altDispatchCourier ? ` · ${order.altDispatchCourier}` : ''}</dd></div>
          <div><dt className="text-[11px] uppercase text-slate-400">Re-dispatch remarks</dt><dd className="text-slate-800">{order.altDispatchRemarks}</dd></div>
          <div className="sm:col-span-2 text-[11px] text-slate-400">{[order.altDispatchByName, fmt(order.altDispatchAt)].filter(Boolean).join(' · ')}</div>
        </>}
      </dl>
      {open && (
        <Modal isOpen onClose={() => setOpen(false)} title="Dispatched via another AWB / service provider">
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs font-medium text-slate-600">Reference number (new AWB / docket) <span className="text-rose-600">*</span></label>
              <input data-testid="alt-dispatch-ref" value={ref} onChange={(e) => setRef(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Service provider</label>
              <input data-testid="alt-dispatch-courier" value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="e.g. DTDC" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Remarks <span className="text-rose-600">*</span></label>
              <textarea data-testid="alt-dispatch-remarks" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 border border-slate-300 rounded-lg">Back</button>
              <button type="button" data-testid="alt-dispatch-save" disabled={!ref.trim() || remarks.trim().length < 3 || save.isPending}
                onClick={() => save.mutate()} className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
